import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { IPagedQuery } from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import {
  EPaymentOrderType,
  EPayoutType,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPaymentRequestFeature,
  EPaymentRequestStatus,
} from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import {
  addMonths,
  endOfDay,
  startOfDay,
  subDays,
} from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { EContractStatus } from "scenes/Properties/scenes/Contracts/components/ContractCard/components/ContractStatusChip/contractStatusChip.types";
import { STATUS_IS_ENDING_WHEN_IN_MONTHS } from "scenes/Properties/scenes/Contracts/components/ContractCard/ContractCard.utils";
import { END_RENTIO_FOLLOW_UP_DSO } from "utils/payment";

const NOW = new Date();
const START_OF_DAY = startOfDay(NOW);

export enum EPaymentRequestFilterPhase {
  Incoming = "INCOMING",
  Rentio = "RENTIO",
  Action = "ACTION",
}

export enum EPaymentRequestFilterStatus {
  New = "new",
  Approved = "approved",
  Pending = "pending",
  Partial = "partial",
  Failed = "failed",
}

export enum EPaymentRequestFilterFrom {
  PayerDirect = "PAYER_DIRECT",
  BrokerThirdParty = "BROKER_THIRD_PARTY",
}

export enum EPaymentRequestFilterHidden {
  Hidden = "HIDDEN",
  NotHidden = "NOT_HIDDEN",
}

export const getContractStatusFilterV2 = (status: EContractStatus) => {
  switch (status) {
    case EContractStatus.Ending:
      return {
        paymentOrder: {
          contract: {
            and: [
              {
                stopDate: { gte: START_OF_DAY },
              },
              {
                stopDate: {
                  lte: addMonths(START_OF_DAY, STATUS_IS_ENDING_WHEN_IN_MONTHS),
                },
              },
            ],
          },
        },
      };

    case EContractStatus.Upcoming:
      return {
        paymentOrder: { contract: { and: [{ startDate: { gte: NOW } }] } },
      };

    case EContractStatus.Running:
      return {
        paymentOrder: {
          contract: {
            and: [
              {
                startDate: { lte: NOW },
              },

              {
                stopDate: {
                  gte: addMonths(START_OF_DAY, STATUS_IS_ENDING_WHEN_IN_MONTHS),
                },
              },
            ],
          },
        },
      };

    case EContractStatus.Finished:
      return {
        paymentOrder: {
          contract: {
            and: [{ stopDate: { lte: NOW } }],
          },
        },
      };

    default:
      return undefined;
  }
};

export const getContractStatusFilter = (status: EContractStatus) => {
  switch (status) {
    case EContractStatus.Ending:
      return {
        and: [
          {
            stopDate: { gte: START_OF_DAY },
          },
          {
            stopDate: {
              lte: addMonths(START_OF_DAY, STATUS_IS_ENDING_WHEN_IN_MONTHS),
            },
          },
        ],
      };

    case EContractStatus.Upcoming:
      return {
        and: [{ startDate: { gte: NOW } }],
      };

    case EContractStatus.Running:
      return {
        and: [
          {
            startDate: { lte: NOW },
          },

          {
            stopDate: {
              gte: addMonths(START_OF_DAY, STATUS_IS_ENDING_WHEN_IN_MONTHS),
            },
          },
        ],
      };

    case EContractStatus.Finished:
      return {
        and: [{ stopDate: { lte: NOW } }],
      };

    default:
      return undefined;
  }
};

export const getPaymentTypesFilter = (paymentTypes: EPaymentOrderType[]) => {
  return {
    type: { inq: paymentTypes },
  };
};

export const getPaymentTypesFilterV2 = (paymentTypes: EPaymentOrderType[]) => {
  return {
    paymentOrder: {
      type: {
        in: paymentTypes,
      },
    },
  };
};

export const getStatusesForFilterStatus = (
  status: EPaymentRequestFilterStatus,
): EPaymentRequestStatus[] => {
  switch (status) {
    case EPaymentRequestFilterStatus.New:
      return [EPaymentRequestStatus.New];

    case EPaymentRequestFilterStatus.Approved:
      return [EPaymentRequestStatus.Approved];

    case EPaymentRequestFilterStatus.Pending: // "In progress"
      return [
        EPaymentRequestStatus.Pending,
        EPaymentRequestStatus.PendingThirdParty,
        EPaymentRequestStatus.PayInPlanned,
        EPaymentRequestStatus.PaidIn,
        EPaymentRequestStatus.Transferring,
        EPaymentRequestStatus.Transferred,
        EPaymentRequestStatus.PayingOut,
        EPaymentRequestStatus.Paid,
      ];

    case EPaymentRequestFilterStatus.Partial:
      return [EPaymentRequestStatus.Partial];

    case EPaymentRequestFilterStatus.Failed:
      return [
        EPaymentRequestStatus.FailedPaidIn,
        EPaymentRequestStatus.FailedTransfer,
        EPaymentRequestStatus.FailedPayout,
        EPaymentRequestStatus.FailedKyc,
        EPaymentRequestStatus.Failed,
      ];

    default:
      return [];
  }
};

export const getPaymentRequestsFilter = (params: {
  propertyId?: number;
  search?: string;
  status?: EPaymentRequestFilterStatus;
  contractStatus?: EContractStatus;
  phase?: EPaymentRequestFilterPhase;
  from?: EPaymentRequestFilterFrom;
  payoutType?: EPayoutType;
  paymentTypes?: EPaymentOrderType[];
}) => {
  const {
    propertyId,
    search,
    status,
    contractStatus,
    phase,
    from,
    paymentTypes,
    payoutType,
  } = params;

  let filters: {}[] = [];

  if (propertyId) {
    filters.push({ requestPropertyId: propertyId });
  }

  const contractWhere = contractStatus
    ? getContractStatusFilter(contractStatus)
    : undefined;
  const paymentOrderWhere =
    paymentTypes && paymentTypes?.length > 0
      ? getPaymentTypesFilter(paymentTypes)
      : undefined;

  if (status) {
    const statuses = getStatusesForFilterStatus(status);

    filters.push({
      status: { inq: statuses },
    });
  }

  if (phase) {
    switch (phase) {
      case EPaymentRequestFilterPhase.Incoming:
        filters.push({ dueDateAt: { gte: startOfDay(NOW.getTime()) } });

        break;

      case EPaymentRequestFilterPhase.Rentio:
        filters.push(
          ...[
            {
              dueDateAt: {
                gt: endOfDay(subDays(NOW, END_RENTIO_FOLLOW_UP_DSO)).getTime(),
              },
            },
            {
              dueDateAt: {
                lt: startOfDay(NOW).getTime(),
              },
            },
          ],
        );

        break;

      case EPaymentRequestFilterPhase.Action:
        filters.push({
          dueDateAt: {
            lte: endOfDay(subDays(NOW, END_RENTIO_FOLLOW_UP_DSO)).getTime(),
          },
        });

        break;
    }
  }

  if (from) {
    switch (from) {
      case EPaymentRequestFilterFrom.PayerDirect:
        filters.push({
          features: {
            nlike: `%${EPaymentRequestFeature.ThirdPartyPayInBrokerDirect}%`,
          },
        });

        break;

      case EPaymentRequestFilterFrom.BrokerThirdParty:
        filters.push({
          features: {
            like: `%${EPaymentRequestFeature.ThirdPartyPayInBrokerDirect}%`,
          },
        });

        break;

      default:
        break;
    }
  }

  if (payoutType) {
    filters.push({
      payoutType: {
        eq: payoutType,
      },
    });
  }

  return {
    order: "dueDateAt ASC, id ASC",
    search,
    where: {
      or: [
        {
          and: [...filters, { status: { neq: EPaymentRequestStatus.Paid } }],
        },
        {
          and: [
            ...filters,

            { status: { eq: EPaymentRequestStatus.Paid } },

            { plannedExecutionAt: { gte: START_OF_DAY } },

            {
              features: {
                like: `%${EPaymentRequestFeature.PlannedPayOut}%`,
              },
            },
          ],
        },
      ],
    },
    contractWhere,
    paymentOrderWhere,
  };
};

export const getPaymentRequestsFilterV2 = (params: {
  propertyId?: number;
  search?: string;
  status?: EPaymentRequestFilterStatus;
  contractStatus?: EContractStatus;
  phase?: EPaymentRequestFilterPhase;
  from?: EPaymentRequestFilterFrom;
  payoutType?: EPayoutType;
  paymentTypes?: EPaymentOrderType[];
  requestPayerAccountId?: number;
  requestPayerAccountIds?: number[];
  hidden?: EPaymentRequestFilterHidden;
}): IPagedQuery => {
  const {
    propertyId,
    search,
    status,
    contractStatus,
    phase,
    from,
    paymentTypes,
    payoutType,
    requestPayerAccountId,
    requestPayerAccountIds,
    hidden,
  } = params;

  let filters: {}[] = [];

  if (propertyId) {
    filters.push({ requestPropertyId: { eq: propertyId } });
  }

  if (contractStatus) {
    const contractStatusFilterMapped =
      getContractStatusFilterV2(contractStatus);

    if (contractStatusFilterMapped) {
      filters.push(contractStatusFilterMapped);
    }
  }

  if (paymentTypes && paymentTypes?.length > 0) {
    const paymentOrderFilterMapped = getPaymentTypesFilterV2(paymentTypes);

    if (paymentOrderFilterMapped) {
      filters.push(paymentOrderFilterMapped);
    }
  }

  if (status) {
    const statuses = getStatusesForFilterStatus(status);

    filters.push({
      status: { in: statuses },
    });
  }

  if (phase) {
    switch (phase) {
      case EPaymentRequestFilterPhase.Incoming:
        filters.push({ dueDateAt: { gte: startOfDay(NOW.getTime()) } });

        break;

      case EPaymentRequestFilterPhase.Rentio:
        filters.push(
          ...[
            {
              dueDateAt: {
                gt: endOfDay(subDays(NOW, END_RENTIO_FOLLOW_UP_DSO)),
              },
            },
            {
              dueDateAt: {
                lt: startOfDay(NOW),
              },
            },
          ],
        );

        break;

      case EPaymentRequestFilterPhase.Action:
        filters.push({
          dueDateAt: {
            lte: endOfDay(subDays(NOW, END_RENTIO_FOLLOW_UP_DSO)),
          },
        });

        break;
    }
  }

  if (from) {
    switch (from) {
      case EPaymentRequestFilterFrom.PayerDirect:
        filters.push({
          features: {
            notlike: `%${EPaymentRequestFeature.ThirdPartyPayInBrokerDirect}%`,
          },
        });

        break;

      case EPaymentRequestFilterFrom.BrokerThirdParty:
        filters.push({
          features: {
            like: `%${EPaymentRequestFeature.ThirdPartyPayInBrokerDirect}%`,
          },
        });

        break;

      default:
        break;
    }
  }

  if (payoutType) {
    filters.push({
      payoutType: {
        eq: payoutType,
      },
    });
  }

  if (requestPayerAccountId) {
    filters.push({
      requestPayerAccountId: {
        eq: requestPayerAccountId,
      },
    });
  }

  if (requestPayerAccountIds) {
    filters.push({
      requestPayerAccountId: {
        in: requestPayerAccountIds,
      },
    });
  }

  const customFilters: string[] = [];
  if (hidden === EPaymentRequestFilterHidden.Hidden) {
    customFilters.push(EPaymentRequestFilterHidden.Hidden);
  } else if (hidden === EPaymentRequestFilterHidden.NotHidden) {
    customFilters.push(EPaymentRequestFilterHidden.NotHidden);
  }

  return {
    limit: CONFIG.DEFAULT_FETCH_LIMIT,
    page: 1,
    sort: [
      { field: "dueDateAt", method: "ASC" },
      { field: "id", method: "ASC" },
    ],
    search,
    ...(customFilters.length > 0 ? { customFilters } : {}),
    filter: {
      or: [
        {
          and: [...filters, { status: { neq: EPaymentRequestStatus.Paid } }],
        },
        {
          and: [
            ...filters,

            { status: { eq: EPaymentRequestStatus.Paid } },

            { plannedExecutionAt: { gte: START_OF_DAY } },

            {
              features: {
                like: `%${EPaymentRequestFeature.PlannedPayOut}%`,
              },
            },
          ],
        },
      ],
    },
  };
};
